// Panel derecho «Nuevo código»: crea el código; el error del servidor va a su campo o a un toast.
import { useMemo } from 'react';
import { toast } from 'sonner';
import { ChoiceChips, EditorField } from '@/shared/patterns/EditorForm';
import type { DiscountCode } from '../api/promotionsContract';
import { useCreateDiscountCode } from '../api/useDiscountCodes';
import type { PromotionFormSources } from '../api/usePromotionFormSources';
import { DISCOUNT_CODE_RETAILER_HELP } from '../constants/helpText';
import {
    CODE_FORM_MESSAGES,
    discountCodeSaveError,
    emptyDiscountCodeForm,
    toCreateDiscountCodeRequest,
    type DiscountCodeFormValues,
} from '../lib/discountCodeForm';
import { DiscountCodeForm } from './DiscountCodeForm';

/** La cadena del código nuevo, para el admin de tenant; sin ella manda la del token. */
export interface DiscountCodeRetailerChoice {
    options: readonly { value: string; label: string }[];
    value: string;
    onChange: (retailerId: string) => void;
}

export interface DiscountCodeCreatePanelProps {
    sources: PromotionFormSources;
    /** Día CDMX `YYYY-MM-DD`: la vigencia arranca hoy. */
    today: string;
    retailer?: DiscountCodeRetailerChoice;
    onCreated: (code: DiscountCode & { retailerId?: string }) => void;
    onCancel: () => void;
}

export function DiscountCodeCreatePanel({
    sources,
    today,
    retailer,
    onCreated,
    onCancel,
}: DiscountCodeCreatePanelProps) {
    const create = useCreateDiscountCode();
    const defaultValues = useMemo(
        () => ({ ...emptyDiscountCodeForm(), validFrom: today }),
        [today],
    );
    const saveError = useMemo(() => {
        const error = create.error ? discountCodeSaveError(create.error) : null;
        return error?.field ? error : null;
    }, [create.error]);

    const submit = (values: DiscountCodeFormValues) => {
        const retailerId = retailer?.value;
        create.mutate(
            { request: toCreateDiscountCodeRequest(values), ...(retailerId ? { retailerId } : {}) },
            {
                onSuccess: (code) => {
                    toast.success('Código creado', { description: code.codeText });
                    onCreated(retailerId ? { ...code, retailerId } : code);
                },
                onError: (error) => {
                    const { field, message } = discountCodeSaveError(error);
                    if (field === null)
                        toast.error(CODE_FORM_MESSAGES.saveErrorTitle, { description: message });
                },
            },
        );
    };

    return (
        <DiscountCodeForm
            // Tiendas y equipos son de la cadena: al cambiarla la forma empieza de nuevo.
            key={retailer?.value ?? 'own'}
            sources={sources}
            defaultValues={defaultValues}
            today={today}
            isSaving={create.isPending}
            saveError={saveError}
            onSubmit={submit}
            onCancel={onCancel}
            disabled={retailer !== undefined && retailer.value === ''}
            retailerField={
                retailer ? (
                    <EditorField
                        label="Cadena"
                        help={DISCOUNT_CODE_RETAILER_HELP}
                        className="mb-3.5"
                    >
                        <ChoiceChips
                            options={retailer.options}
                            value={retailer.value}
                            onPick={retailer.onChange}
                        />
                    </EditorField>
                ) : undefined
            }
        />
    );
}
